// ROOT: frontend/src/pages/GetStarted.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Instagram,
  Music2,
  ChevronRight,
  Check,
  TrendingUp,
  MessageCircle,
  Mail as MailIcon,
  Tag,
  Copy,
} from 'lucide-react';
import { api } from '../api';
import { useToast } from '../hooks/use-toast';

const LiveCounter = ({ handle }) => {
  const target = useMemo(() => {
    const base = 2400 + (handle.length * 137) % 1800;
    return base;
  }, [handle]);

  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!handle) {
      setValue(0);
      return;
    }

    setValue(0);

    const start = performance.now();
    const dur = 1600;
    let raf;

    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);

      setValue(Math.round(target * eased));

      if (p < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [handle, target]);

  if (!handle) return null;

  return (
    <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 border border-slate-200 fade-in-up">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
        <TrendingUp className="w-4 h-4 text-emerald-500" />
        Live growth preview
      </div>

      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-3xl font-extrabold plixy-gradient-text">
          +{value.toLocaleString()}
        </span>

        <span className="text-sm text-slate-600 font-semibold">
          projected followers / month for @{handle}
        </span>
      </div>
    </div>
  );
};

const PlanBanner = ({ planName, price, range, billing }) => {
  if (!planName) return null;

  return (
    <div className="mb-4 p-4 rounded-2xl bg-white border border-slate-200 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl plixy-gradient-ring text-white flex items-center justify-center">
          <Tag className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Selected plan
          </div>

          <div className="font-extrabold text-[#0a1930] truncate">
            {planName} — ${price}/mo
            {billing === 'yearly' ? ' (billed yearly)' : ''}
          </div>

          {range && (
            <div className="text-xs text-slate-500 truncate">
              {range}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ContactOptions = ({
  social,
  brandName,
  planName,
  price,
  platform,
  handle,
}) => {
  const platformName = platform === 'instagram' ? 'Instagram' : 'TikTok';
  const msg = `Hi ${brandName} team! 👋 I’m interested in the ${
    planName || 'growth'
  } plan${price ? ` ($${price}/mo)` : ''}. My ${platformName} username is @${handle}. I’m ready to get started. Please share the next steps. 🚀`;

  const encoded = encodeURIComponent(msg);

  const waNumber = (social?.whatsapp_number || '').replace(
    /[^0-9]/g,
    ''
  );

  const waLink = waNumber
    ? `https://wa.me/${waNumber}?text=${encoded}`
    : '';

  const igLink = social?.instagram_url || '';

  const mailTo = social?.contact_email
    ? `mailto:${social.contact_email}?subject=${encodeURIComponent(
        `Ready to start - ${planName || 'Growth'} plan`
      )}&body=${encoded}`
    : '';

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(msg);
      toast({
        title: 'Message copied!',
        description: 'Paste it into Instagram DM if needed.',
      });
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Please select and copy the message manually.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="mt-6 p-5 rounded-2xl bg-white border border-slate-200 shadow-xl fade-in-up">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
        You’re ready to grow 🚀
      </div>

      <h3 className="font-extrabold text-[#0a1930] text-xl leading-snug">
        Let’s get your growth started
      </h3>

      <p className="mt-2 text-sm text-slate-600">
        Your username is all we need. Pick your favorite way to connect and our team will take it from here.
      </p>

      <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Your ready-to-send message
          </div>

          <button
            type="button"
            onClick={copyMessage}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0a1930] hover:opacity-70 transition-opacity"
          >
            <Copy className="w-3.5 h-3.5" />
            Copy
          </button>
        </div>

        <p className="text-sm leading-relaxed text-[#0a1930]">
          {msg}
        </p>
      </div>

      <div className="mt-4 grid gap-3">
        <a
          href={waLink || '#'}
          onClick={(e) => {
            if (!waLink) {
              e.preventDefault();
              alert('WhatsApp number not configured yet.');
            }
          }}
          target="_blank"
          rel="noreferrer"
          className={`group flex items-center gap-3 p-4 rounded-xl border transition-all ${
            waLink
              ? 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 hover:-translate-y-0.5 hover:shadow-md'
              : 'border-slate-200 bg-slate-50 opacity-70'
          }`}
        >
          <div className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow">
            <MessageCircle className="w-5 h-5" />
          </div>

          <div className="flex-1">
            <div className="font-bold text-[#0a1930]">
              Start on WhatsApp
            </div>

            <div className="text-xs text-slate-500">
              Message is ready — just send it
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#0a1930] transition-colors" />
        </a>

        <a
          href={igLink || '#'}
          onClick={(e) => {
            if (!igLink) {
              e.preventDefault();
              alert('Instagram link not configured yet.');
            }
          }}
          target="_blank"
          rel="noreferrer"
          className={`group flex items-center gap-3 p-4 rounded-xl border transition-all ${
            igLink
              ? 'border-pink-200 bg-pink-50/40 hover:bg-pink-50 hover:-translate-y-0.5 hover:shadow-md'
              : 'border-slate-200 bg-slate-50 opacity-70'
          }`}
        >
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-amber-400 text-white flex items-center justify-center shadow">
            <Instagram className="w-5 h-5" />
          </div>

          <div className="flex-1">
            <div className="font-bold text-[#0a1930]">
              DM us on Instagram
            </div>

            <div className="text-xs text-slate-500">
              Open DM and send your ready message
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#0a1930] transition-colors" />
        </a>

        <a
          href={mailTo || '#'}
          onClick={(e) => {
            if (!mailTo) {
              e.preventDefault();
              alert('Contact email not configured yet.');
            }
          }}
          className={`group flex items-center gap-3 p-4 rounded-xl border transition-all ${
            mailTo
              ? 'border-blue-200 bg-blue-50/40 hover:bg-blue-50 hover:-translate-y-0.5 hover:shadow-md'
              : 'border-slate-200 bg-slate-50 opacity-70'
          }`}
        >
          <div className="w-11 h-11 rounded-xl bg-[#0a1930] text-white flex items-center justify-center shadow">
            <MailIcon className="w-5 h-5" />
          </div>

          <div className="flex-1">
            <div className="font-bold text-[#0a1930]">
              Continue by Email
            </div>

            <div className="text-xs text-slate-500">
              Opens email with your message ready
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#0a1930] transition-colors" />
        </a>
      </div>
    </div>
  );
};

const GetStarted = () => {
  const [params] = useSearchParams();

  const initialPlatform =
    params.get('platform') === 'tiktok'
      ? 'tiktok'
      : 'instagram';

  const planName = params.get('name') || '';
  const planPrice = params.get('price') || '';
  const planRange = params.get('range') || '';
  const billing = params.get('billing') || '';

  const [platform, setPlatform] = useState(initialPlatform);
  const [handle, setHandle] = useState('');
  const [done, setDone] = useState(false);

  const [result, setResult] = useState(null);

  const [brandName, setBrandName] = useState('IGGrowth');

  const [social, setSocial] = useState({
    whatsapp_number: '',
    instagram_url: '',
    contact_email: '',
  });

  const { toast } = useToast();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await api.getPublicSettings();

        setBrandName(
          settings?.brandName ||
          settings?.brand_name ||
          'IGGrowth'
        );

        setSocial({
          whatsapp_number:
            settings?.whatsappNumber ||
            settings?.whatsapp_number ||
            '',
          instagram_url:
            settings?.instagramUrl ||
            settings?.instagram_url ||
            '',
          contact_email:
            settings?.contactEmail ||
            settings?.contact_email ||
            '',
        });
      } catch (error) {
        console.error('Failed to load contact settings:', error);
      }
    };

    loadSettings();
  }, []);

  const submit = (e) => {
    e.preventDefault();

    if (!handle.trim()) {
      return toast({
        title: 'Enter your username',
        description: `Add your ${platform === 'instagram' ? 'Instagram' : 'TikTok'} username to continue.`,
        variant: 'destructive',
      });
    }

    const cleanHandle = handle.replace('@', '').trim();

    setResult({
      platform,
      handle: cleanHandle,
    });

    setDone(true);

    toast({
      title: 'You’re in! 🚀',
      description: 'Choose how you want to start your growth.',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40 flex flex-col">

      <header className="px-5 lg:px-8 py-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2"
        >
          <div className="relative w-8 h-8">
            <div className="absolute inset-0 rounded-full plixy-gradient-ring" />
            <div className="absolute inset-[3px] rounded-full bg-white" />
            <div className="absolute inset-[7px] rounded-full plixy-gradient-ring opacity-90" />
          </div>

          <span className="text-2xl font-extrabold text-[#0a1930]">
            {brandName}
          </span>
        </Link>
      </header>

      <div className="flex-1 flex items-start justify-center px-5 py-10">
        <div className="w-full max-w-lg">

          {planName && (
            <PlanBanner
              planName={planName}
              price={planPrice}
              range={planRange}
              billing={billing}
            />
          )}

          {!done ? (
            <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 md:p-10 fade-in-up">

              <div className="flex items-center gap-2 mb-6">
                {platform === 'instagram' ? (
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-amber-400 text-white flex items-center justify-center">
                    <Instagram className="w-5 h-5" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-[#0a1930] text-white flex items-center justify-center">
                    <Music2 className="w-5 h-5" />
                  </div>
                )}

                <h1 className="text-2xl md:text-3xl font-extrabold text-[#0a1930]">
                  Ready to Start Growing?
                </h1>
              </div>

              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-full mb-6">
                <button
                  type="button"
                  onClick={() => setPlatform('instagram')}
                  className={`py-2.5 rounded-full text-sm font-bold transition-all ${
                    platform === 'instagram'
                      ? 'bg-white shadow text-[#0a1930]'
                      : 'text-slate-500'
                  }`}
                >
                  Instagram
                </button>

                <button
                  type="button"
                  onClick={() => setPlatform('tiktok')}
                  className={`py-2.5 rounded-full text-sm font-bold transition-all ${
                    platform === 'tiktok'
                      ? 'bg-white shadow text-[#0a1930]'
                      : 'text-slate-500'
                  }`}
                >
                  TikTok
                </button>
              </div>

              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-[#0a1930] mb-2">
                    Your {platform === 'instagram' ? 'Instagram' : 'TikTok'} Username
                  </label>

                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">
                      @
                    </span>

                    <input
                      value={handle}
                      onChange={(e) =>
                        setHandle(e.target.value.replace(/\s/g, ''))
                      }
                      placeholder="yourusername"
                      autoComplete="off"
                      className="w-full pl-8 pr-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-[#0a1930] focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
                    />
                  </div>

                  <LiveCounter handle={handle.trim()} />
                </div>

                <button
                  type="submit"
                  className="w-full plixy-dark-btn py-3.5 rounded-xl font-semibold inline-flex items-center justify-center gap-1"
                >
                  Let’s Get Started
                  <ChevronRight className="w-5 h-5" />
                </button>

                <p className="text-xs text-slate-500 text-center">
                  No password needed. Just your username and you’re ready to go.
                </p>
              </form>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 md:p-10 fade-in-up">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
                  <Check className="w-8 h-8" strokeWidth={3} />
                </div>

                <h2 className="text-3xl font-extrabold text-[#0a1930] text-center">
                  You’re ready, @{result?.handle}!
                </h2>

                <p className="mt-2 text-slate-600 text-center">
                  Pick a contact option below and let’s get your growth started.
                </p>
              </div>

              <ContactOptions
                social={social}
                brandName={brandName}
                planName={planName}
                price={planPrice}
                platform={result?.platform}
                handle={result?.handle}
              />

              <button
                onClick={() => {
                  window.location.href = '/';
                }}
                className="mt-4 w-full plixy-dark-btn px-6 py-3 rounded-full font-semibold"
              >
                Back to home
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GetStarted;
